import { createContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import { useSession } from '@/shared/session';

import type { TenantContextValue, TenantInfo } from './tenantTypes';

/**
 * Multi-tenant context.
 *
 * Projects the application {@link useSession} (tenant memberships + active tenant)
 * onto the tenant-facing contract. The **active tenant is authoritative server-side**
 * (held in the BFF session); switching tenants calls the BFF, which validates
 * membership — the client never asserts a trusted tenant via a header.
 */

// Context co-located with its provider (see required project structure);
// the hook lives in useTenant.ts.
// eslint-disable-next-line react-refresh/only-export-components
export const TenantContext = createContext<TenantContextValue | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const session = useSession();

  const value = useMemo<TenantContextValue>(
    () => ({
      tenantId: session.activeTenant?.id ?? null,
      tenantName: session.activeTenant?.name ?? null,
      tenant: session.activeTenant,
      availableTenants: session.tenants,
      setTenant: (tenant: TenantInfo | null) => {
        if (tenant) {
          void session.setActiveTenant(tenant.id);
        }
      },
    }),
    [session],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}
